import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';
import PublicNav from '../components/PublicNav';

function Contact() {
  const { getAppName } = useConfig();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/contact`, formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <PublicNav />

      {/* Hero Section */}
      <section className="contact-hero">
        <h1>Contact Us</h1>
        <p className="hero-subtitle">
          Have questions? We'd love to hear from you.
        </p>
      </section>

      {/* Contact Content */}
      <section className="contact-content">
        <div className="contact-container">
          {/* Contact Info */}
          <div className="contact-info">
            <h2>Get in Touch</h2>
            <p>
              Whether you have questions about features, pricing, or need help
              getting started, our team is ready to help.
            </p>

            <div className="contact-methods">
              <div className="contact-method">
                <div className="method-icon">📧</div>
                <div>
                  <h3>Email</h3>
                  <p>support@lawntrack.app</p>
                </div>
              </div>
              <div className="contact-method">
                <div className="method-icon">💬</div>
                <div>
                  <h3>Live Chat</h3>
                  <p>Available Mon-Fri, 9am-5pm EST</p>
                </div>
              </div>
              <div className="contact-method">
                <div className="method-icon">📚</div>
                <div>
                  <h3>Help Center</h3>
                  <p>Browse our documentation</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-wrapper">
            {submitted ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Message Sent!</h3>
                <p>Thanks for reaching out. We'll get back to you within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} className="btn-reset">
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                {error && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '8px', fontSize: '0.9rem' }}>
                    {error}
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@company.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Question</option>
                    <option value="sales">Sales Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="billing">Billing Question</option>
                    <option value="feedback">Feedback</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="How can we help?"
                  />
                </div>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="page-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="logo-icon">🌿</span>
            <span>{getAppName()}</span>
          </div>
          <div className="footer-links">
            <Link to="/about">About</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <p className="footer-copy">© {new Date().getFullYear()} {getAppName()}. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        .contact-page {
          min-height: 100vh;
          background: #ffffff;
          padding-top: 64px;
        }

        .logo-icon {
          font-size: 1.5rem;
        }

        /* Hero */
        .contact-hero {
          background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%);
          color: white;
          text-align: center;
          padding: 60px 24px;
        }

        .contact-hero h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin: 0 0 16px;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          opacity: 0.9;
          margin: 0;
        }

        /* Content */
        .contact-content {
          padding: 60px 24px;
        }

        .contact-container {
          max-width: 1000px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
        }

        /* Contact Info */
        .contact-info h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin: 0 0 16px;
        }

        .contact-info > p {
          color: #6b7280;
          line-height: 1.6;
          margin: 0 0 32px;
        }

        .contact-methods {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .contact-method {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }

        .method-icon {
          font-size: 1.5rem;
          width: 48px;
          height: 48px;
          background: #f0fdf4;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .contact-method h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 4px;
        }

        .contact-method p {
          font-size: 0.95rem;
          color: #6b7280;
          margin: 0;
        }

        /* Form */
        .contact-form-wrapper {
          background: #f9fafb;
          border-radius: 16px;
          padding: 32px;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 0.9rem;
          font-weight: 500;
          color: #374151;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #4CAF50;
          box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
        }

        .form-group textarea {
          resize: vertical;
        }

        .btn-submit {
          background: #4CAF50;
          color: white;
          font-weight: 600;
          padding: 14px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-submit:hover {
          background: #388E3C;
        }

        /* Success Message */
        .success-message {
          text-align: center;
          padding: 40px 20px;
        }

        .success-icon {
          width: 64px;
          height: 64px;
          background: #4CAF50;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin: 0 auto 24px;
        }

        .success-message h3 {
          font-size: 1.25rem;
          color: #111827;
          margin: 0 0 12px;
        }

        .success-message p {
          color: #6b7280;
          margin: 0 0 24px;
        }

        .btn-reset {
          background: transparent;
          color: #4CAF50;
          border: 2px solid #4CAF50;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-reset:hover {
          background: #4CAF50;
          color: white;
        }

        /* Footer */
        .page-footer {
          background: #111827;
          color: white;
          padding: 40px 24px;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          text-align: center;
        }

        .footer-brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .footer-links {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin-bottom: 16px;
        }

        .footer-links a {
          color: #9ca3af;
          text-decoration: none;
          transition: color 0.2s;
        }

        .footer-links a:hover {
          color: white;
        }

        .footer-copy {
          color: #6b7280;
          font-size: 0.9rem;
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .contact-container {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .contact-hero h1 {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
}

export default Contact;
